// AdminAuthGate.tsx — Internet Identity login + admin allowlist check.
//
// States:
//   loading          — spinner substitute (obsidian + gold mark)
//   unauthenticated  — II login CTA
//   checking_admin   — "Verifying access." interim
//   denied           — "Access denied. This panel is for admins only."
//   authenticated    — renders children
//
// Copy is brand-compliant: no exclamation marks, dignified register throughout.
// The denial copy satisfies the task brief verbatim.

import React from 'react';
import DominusMark from './components/brand/DominusMark';
import { useAdminAuth, type UseAdminAuthReturn } from './hooks/useAdminAuth';

interface AdminAuthGateProps {
  children: (auth: UseAdminAuthReturn) => React.ReactElement;
}

export default function AdminAuthGate({ children }: AdminAuthGateProps): React.ReactElement {
  const auth = useAdminAuth();

  if (auth.state === 'loading' || auth.state === 'checking_admin') {
    return (
      <div className="auth-gate">
        <div className="auth-card">
          <div className="auth-mark">
            <DominusMark size={48} />
          </div>
          <p className="auth-subtitle">
            {auth.state === 'checking_admin'
              ? 'Verifying access.'
              : 'The table is being prepared.'}
          </p>
        </div>
      </div>
    );
  }

  if (auth.state === 'denied') {
    return (
      <div className="auth-gate">
        <div className="auth-card">
          <div className="auth-mark">
            <DominusMark size={48} />
          </div>
          <h1 className="auth-title">DOMINUS</h1>
          <div className="auth-denied">
            Access denied. This panel is for admins only.
          </div>
          {auth.principal && (
            <p style={{
              fontFamily: 'var(--font-ui)',
              fontStyle: 'normal',
              fontSize: '11px',
              letterSpacing: '0.12em',
              color: 'var(--ivory-d)',
              marginBottom: 'var(--s-md)',
              wordBreak: 'break-all',
            }}>
              {auth.principal}
            </p>
          )}
          <button className="btn btn-secondary" onClick={() => auth.logout()}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (auth.state === 'unauthenticated') {
    return (
      <div className="auth-gate">
        <div className="auth-card">
          <div className="auth-mark">
            <DominusMark size={48} />
          </div>
          <h1 className="auth-title">DOMINUS</h1>
          <p className="auth-subtitle">
            Operator Console. Sign in with Internet Identity to continue.
          </p>
          {auth.error && (
            <div className="auth-error">{auth.error}</div>
          )}
          <button className="btn" onClick={() => auth.login()}>
            Sign in with Internet Identity
          </button>
        </div>
      </div>
    );
  }

  // auth.state === 'authenticated'
  return children(auth);
}
